import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  getWalletPortfolioCoin,
  getWalletWithdrawCoinNetworks,
  getWalletWithdrawRequestId,
  postWalletWithdraw,
} from "../api/endpoints/wallet/wallet";
import type { CreateWithdrawRequestInput } from "../api/models/createWithdrawRequestInput";
import type { PortfolioCoin } from "../api/models/portfolioCoin";
import type { WithdrawNetwork } from "../api/models/withdrawNetwork";
import type { WithdrawRequest } from "../api/models/withdrawRequest";
import type { WithdrawRequestStatus } from "../api/models/withdrawRequestStatus";
import { getBearerToken } from "../lib/authClient";
import { toNumber } from "../lib/numberFormat";
import {
  useWithdrawQuote,
  WithdrawQuoteError,
} from "../hooks/use_withdraw_quote";

import "./WithdrawPage.css";

interface LocationState {
  code?: string;
  name?: string;
  icon?: string;
}

const authHeaders = () => {
  const token = getBearerToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
};

// Estados em que o saque já se encerrou — o polling para ao atingir um deles.
const TERMINAL_STATUSES: WithdrawRequestStatus[] = [
  "COMPLETED",
  "FAILED",
  "CANCELLED",
];

const WITHDRAW_STATUS_LABEL: Record<WithdrawRequestStatus, string> = {
  PENDING: "Pendente",
  TRANSFERRING: "Transferência interna",
  TRANSFER_DONE: "Transferência concluída",
  SUBMITTING: "Enviando à rede",
  SUBMITTED: "Submetido à rede",
  COMPLETED: "Concluído",
  FAILED: "Falhou",
  COMPENSATING: "Compensando saldo",
  CANCELLED: "Cancelado",
};

const POLL_INTERVAL_MS = 5000;

const formatQty = (n: number) =>
  n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  });

const truncateAddress = (addr: string) => {
  if (addr.length <= 16) return addr;
  return `${addr.slice(0, 8)}...${addr.slice(-6)}`;
};

// Mantém no máximo `max` casas decimais conforme a precisão da rede (minAccuracy).
const limitDecimals = (v: string, max: number): string => {
  const sepIdx = Math.max(v.lastIndexOf("."), v.lastIndexOf(","));
  if (sepIdx === -1) return v;
  return v.slice(0, sepIdx + 1) + v.slice(sepIdx + 1, sepIdx + 1 + max);
};

export default function WithdrawPage() {
  const navigate = useNavigate();
  const params = useParams<{ coin: string }>();
  const location = useLocation();
  const meta = (location.state as LocationState | null) ?? null;
  const coinCode = (params.coin ?? "").toUpperCase();

  const [portfolio, setPortfolio] = useState<PortfolioCoin | null>(null);
  const [networks, setNetworks] = useState<WithdrawNetwork[]>([]);
  const [selectedNetwork, setSelectedNetwork] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<WithdrawRequest | null>(null);
  const [statusUpdatedAt, setStatusUpdatedAt] = useState<Date | null>(null);

  // Acompanhamento do saque: enquanto o status não for terminal, consulta o
  // detalhe (GET /wallet/withdraw/request/:id) a cada ciclo e reflete os novos
  // dados (status, txHash, confirmações, motivo de falha) no card de resultado.
  const withdrawId = result?.id ?? null;
  const withdrawStatus = result?.status ?? null;
  const isTerminal = withdrawStatus !== null &&
    TERMINAL_STATUSES.includes(withdrawStatus);

  useEffect(() => {
    if (!withdrawId || isTerminal) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const resp = await getWalletWithdrawRequestId(withdrawId, authHeaders());
        if (cancelled) return;
        if (resp.status === 200 && "data" in resp.data) {
          setResult(resp.data.data);
          setStatusUpdatedAt(new Date());
        }
      } catch (err) {
        console.warn("Falha ao atualizar status do saque:", err);
      }
    };

    const handle = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [withdrawId, isTerminal]);

  useEffect(() => {
    if (!coinCode) return;
    let cancelled = false;

    const fetchInitial = async () => {
      setLoading(true);
      setError(null);
      try {
        const [networksResp, portfolioResp] = await Promise.all([
          getWalletWithdrawCoinNetworks(coinCode, authHeaders()),
          getWalletPortfolioCoin(coinCode, authHeaders()),
        ]);
        if (cancelled) return;

        if (networksResp.status === 200 && "data" in networksResp.data) {
          const list = networksResp.data.data;
          setNetworks(list);
          if (list.length > 0) setSelectedNetwork(list[0].network);
        } else {
          const msg = (networksResp.data as { message?: string })?.message ??
            `Falha ao buscar redes (status ${networksResp.status}).`;
          setError(msg);
        }

        if (portfolioResp.status === 200 && "data" in portfolioResp.data) {
          setPortfolio(portfolioResp.data.data);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Erro ao carregar saque:", err);
        setError(err instanceof Error ? err.message : "Erro desconhecido");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchInitial();
    return () => {
      cancelled = true;
    };
  }, [coinCode]);

  const selectedNetworkMeta = useMemo(
    () => networks.find((n) => n.network === selectedNetwork) ?? null,
    [networks, selectedNetwork],
  );

  // Casas decimais aceitas pela rede; fallback 8 quando minAccuracy não é um
  // inteiro válido (a Bybit costuma expor a precisão como nº de casas).
  const networkDecimals = useMemo(() => {
    const raw = selectedNetworkMeta?.minAccuracy;
    const n = raw !== undefined ? parseInt(raw, 10) : NaN;
    return Number.isInteger(n) && n >= 0 && n <= 18 ? n : 8;
  }, [selectedNetworkMeta]);

  const available = toNumber(portfolio?.available ?? 0);
  const amountNum = useMemo(() => {
    const n = parseFloat(amount.replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  }, [amount]);

  const networkFee = selectedNetworkMeta
    ? parseFloat(selectedNetworkMeta.withdrawFee)
    : NaN;
  const minW = selectedNetworkMeta
    ? parseFloat(selectedNetworkMeta.withdrawMin)
    : null;
  const maxW = selectedNetworkMeta?.withdrawMax
    ? parseFloat(selectedNetworkMeta.withdrawMax)
    : null;

  const addressValid = address.trim().length > 0;

  // Simulação no backend: a taxa e o total debitado saem daqui, com as mesmas
  // validações do saque real (destino permitido, limites da rede).
  const quote = useWithdrawQuote({
    coin: coinCode,
    network: selectedNetwork,
    address,
    amount,
    enabled: !result && !!selectedNetworkMeta,
    options: authHeaders(),
  });

  const quoteData = quote.data ?? null;
  const quoteError = quote.error instanceof WithdrawQuoteError
    ? quote.error
    : null;
  const quoteLoading = quote.isFetching || quote.isDebouncing;

  // Antes da simulação, a taxa da rede serve de estimativa (botão Máx, saldo).
  const fee = quoteData
    ? parseFloat(quoteData.withdrawFee)
    : (Number.isFinite(networkFee) ? networkFee : 0);

  // O valor digitado é o que chega ao destino: a taxa é cobrada por cima.
  const totalDebit = quoteData
    ? parseFloat(quoteData.totalDebit)
    : (amountNum > 0 ? amountNum + fee : 0);

  /** Saldo máximo que pode chegar ao destino (o resto cobre a taxa). */
  const maxReceivable = Math.max(0, available - fee);

  // Pré-validação local, para feedback imediato enquanto a simulação não volta.
  // A autoridade sobre limites e destino é o backend, via quote.
  const amountError = useMemo(() => {
    if (!amount) return null;
    if (amountNum <= 0) return "Informe um valor válido.";
    if (totalDebit > available) {
      return `Saldo insuficiente: o saque debita ${
        formatQty(totalDebit)
      } ${coinCode} (valor + taxa de rede).`;
    }
    if (minW !== null && amountNum < minW) {
      return `Mínimo: ${selectedNetworkMeta?.withdrawMin} ${coinCode}`;
    }
    if (maxW !== null && amountNum > maxW) {
      return `Máximo: ${selectedNetworkMeta?.withdrawMax} ${coinCode}`;
    }
    return null;
  }, [
    amount,
    amountNum,
    available,
    totalDebit,
    minW,
    maxW,
    selectedNetworkMeta,
    coinCode,
  ]);

  // Sem simulação aprovada não há confirmação: é ela que antecipa as recusas
  // que antes só apareciam ao confirmar o saque.
  const canSubmit = !submitting && !result && !!selectedNetwork &&
    addressValid && amountNum > 0 && !amountError && !!quoteData &&
    !quoteLoading;

  const handleAmountChange = (raw: string) => {
    const v = limitDecimals(raw.replace(/[^\d.,]/g, ""), networkDecimals);
    setAmount(v);
  };

  const handleMax = () => {
    if (maxReceivable <= 0) return;
    // Trunca: arredondar para cima estouraria o saldo somado à taxa.
    const fixed = maxReceivable.toFixed(networkDecimals + 2);
    const dotIndex = fixed.indexOf(".");
    const truncated = networkDecimals > 0
      ? fixed.slice(0, dotIndex + 1 + networkDecimals)
      : fixed.slice(0, dotIndex);
    setAmount(truncated.replace(/\.?0+$/, "") || "0");
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body: CreateWithdrawRequestInput = {
        coin: coinCode,
        network: selectedNetwork,
        address: address.trim(),
        amount: amount.replace(",", "."),
      };
      const response = await postWalletWithdraw(body, authHeaders());
      if (
        response.status >= 200 && response.status < 300 &&
        "data" in response.data
      ) {
        setResult(response.data.data);
      } else {
        const msg = (response.data as { message?: string })?.message ??
          `Falha ao solicitar saque (status ${response.status}).`;
        setSubmitError(msg);
      }
    } catch (err) {
      console.error("Erro ao solicitar saque:", err);
      setSubmitError(err instanceof Error ? err.message : "Erro desconhecido");
    } finally {
      setSubmitting(false);
    }
  };

  const coinName = meta?.name ?? portfolio?.coin ?? coinCode;
  const coinIcon = meta?.icon;

  // Tom do card de resultado conforme o status do saque.
  const statusTone: "success" | "error" | "pending" =
    withdrawStatus === "COMPLETED"
      ? "success"
      : withdrawStatus === "FAILED" || withdrawStatus === "CANCELLED"
      ? "error"
      : "pending";
  const resultTitle = withdrawStatus === "COMPLETED"
    ? "Saque concluído"
    : withdrawStatus === "FAILED"
    ? "Saque falhou"
    : withdrawStatus === "CANCELLED"
    ? "Saque cancelado"
    : "Saque em andamento";
  const resultIcon = statusTone === "success"
    ? "✓"
    : statusTone === "error"
    ? "✕"
    : "⏳";
  const statusLabel = withdrawStatus
    ? WITHDRAW_STATUS_LABEL[withdrawStatus]
    : "—";

  return (
    <div className="page-centered">
      <div className="withdraw-wrap">
        <div className="withdraw-topbar">
          <button
            type="button"
            className="withdraw-close"
            onClick={() => navigate(-1)}
            aria-label="Fechar"
          >
            ×
          </button>
          <div className="withdraw-topbar-title">
            <div className="withdraw-topbar-heading">Sacar</div>
            <div className="withdraw-topbar-sub">{coinName}</div>
          </div>
          <span aria-hidden="true" className="withdraw-topbar-spacer" />
        </div>

        {loading && <div className="withdraw-status">Carregando...</div>}

        {error && !loading && (
          <div className="withdraw-status withdraw-status--error">{error}</div>
        )}

        {!loading && !error && result && (
          <div className={`withdraw-success withdraw-success--${statusTone}`}>
            <div
              className={`withdraw-success-icon withdraw-success-icon--${statusTone}`}
              aria-hidden="true"
            >
              {resultIcon}
            </div>
            <div className="withdraw-success-title">{resultTitle}</div>
            <div className="withdraw-success-sub">
              {statusTone === "success"
                ? "Os fundos foram enviados para a rede com sucesso."
                : statusTone === "error"
                ? (result.failureReason ??
                  "Não foi possível concluir o saque. O saldo foi recomposto, se aplicável.")
                : "Acompanhando o status do saque em tempo real. A confirmação on-chain pode levar alguns minutos."}
            </div>

            <div className="withdraw-success-statusBar">
              <span
                className={`withdraw-statusBadge withdraw-statusBadge--${statusTone}`}
              >
                {statusLabel}
              </span>
              {!isTerminal
                ? (
                  <span className="withdraw-live">
                    <span className="withdraw-live-dot" /> ao vivo
                    {statusUpdatedAt
                      ? ` · ${
                        statusUpdatedAt.toLocaleTimeString("pt-BR", {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })
                      }`
                      : ""}
                  </span>
                )
                : <span className="withdraw-live withdraw-live--done">finalizado</span>}
            </div>

            <div className="withdraw-success-card">
              <div className="withdraw-success-row">
                <span>Chega ao destino</span>
                <span>{result.amount} {result.coin}</span>
              </div>
              {result.withdrawFee && (
                <>
                  <div className="withdraw-success-row">
                    <span>Taxa de rede</span>
                    <span>+ {result.withdrawFee} {result.coin}</span>
                  </div>
                  <div className="withdraw-success-row">
                    <span>Total debitado</span>
                    <span>
                      {formatQty(
                        parseFloat(result.amount) +
                          parseFloat(result.withdrawFee),
                      )} {result.coin}
                    </span>
                  </div>
                </>
              )}
              <div className="withdraw-success-row">
                <span>Rede</span>
                <span>{selectedNetworkMeta?.name ?? result.network}</span>
              </div>
              <div className="withdraw-success-row">
                <span>Destino</span>
                <span>{truncateAddress(result.address)}</span>
              </div>
              {result.confirmations !== null && (
                <div className="withdraw-success-row">
                  <span>Confirmações</span>
                  <span>{result.confirmations}</span>
                </div>
              )}
              {result.txHash && (
                <div className="withdraw-success-row">
                  <span>Hash da transação</span>
                  <span className="withdraw-success-mono">
                    {truncateAddress(result.txHash)}
                  </span>
                </div>
              )}
              {result.bybitWithdrawId && (
                <div className="withdraw-success-row">
                  <span>ID Bybit</span>
                  <span className="withdraw-success-mono">
                    {result.bybitWithdrawId}
                  </span>
                </div>
              )}
              {statusTone === "error" && result.failureReason && (
                <div className="withdraw-success-row withdraw-success-row--error">
                  <span>Motivo</span>
                  <span>{result.failureReason}</span>
                </div>
              )}
            </div>

            <button
              type="button"
              className="withdraw-submit"
              onClick={() =>
                navigate(`/wallet/portfolio/${coinCode}`, { replace: true })}
            >
              Voltar à carteira
            </button>
          </div>
        )}

        {!loading && !error && !result && (
          <>
            <div className="withdraw-hero">
              <div className="withdraw-hero-top">
                <span className="withdraw-hero-label">
                  Disponível para saque
                </span>
                <div className="withdraw-hero-coin">
                  {coinIcon && <img src={coinIcon} alt={coinCode} />}
                  <span>{coinCode}</span>
                </div>
              </div>
              <div className="withdraw-hero-amount">
                {formatQty(available)}
              </div>
            </div>

            <div className="withdraw-section">
              <label htmlFor="withdraw-network" className="withdraw-sectionLabel">
                Rede de envio
              </label>
              <div className="withdraw-select">
                <select
                  id="withdraw-network"
                  value={selectedNetwork}
                  onChange={(e) => setSelectedNetwork(e.target.value)}
                  disabled={networks.length === 0}
                >
                  {networks.length === 0 && (
                    <option value="">Nenhuma rede disponível</option>
                  )}
                  {networks.map((net) => (
                    <option key={net.network} value={net.network}>
                      {net.name}
                    </option>
                  ))}
                </select>
                <span className="withdraw-select-chevron" aria-hidden="true">
                  ⌄
                </span>
              </div>
            </div>

            <div className="withdraw-section">
              <label htmlFor="withdraw-address" className="withdraw-sectionLabel">
                Endereço da carteira de destino
              </label>
              <input
                id="withdraw-address"
                type="text"
                className="withdraw-input"
                placeholder={`Cole o endereço ${coinCode}`}
                value={address}
                spellCheck={false}
                autoComplete="off"
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>

            <div className="withdraw-section">
              <label htmlFor="withdraw-amount" className="withdraw-sectionLabel">
                Valor a sacar
              </label>
              <div className="withdraw-amountInput">
                <input
                  id="withdraw-amount"
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                />
                <button
                  type="button"
                  className="withdraw-maxBtn"
                  onClick={handleMax}
                  disabled={available <= 0}
                >
                  Máx
                </button>
                <span className="withdraw-amountInput-coin">{coinCode}</span>
              </div>
              <div className="withdraw-amountMeta">
                <span>
                  Disponível: {formatQty(available)} {coinCode}
                </span>
                {selectedNetworkMeta && (
                  <span>
                    Máx. c/ taxa: {formatQty(maxReceivable)} {coinCode}
                  </span>
                )}
                {selectedNetworkMeta && (
                  <span>
                    Mín: {selectedNetworkMeta.withdrawMin} {coinCode}
                  </span>
                )}
              </div>
              {amountError && (
                <div className="withdraw-fieldError">{amountError}</div>
              )}
            </div>

            {selectedNetworkMeta && (
              <div className="withdraw-fees">
                {!quote.isReady && (
                  <div className="withdraw-fees-hint">
                    Informe o endereço e o valor para calcular a taxa da rede.
                  </div>
                )}

                {quote.isReady && quoteLoading && (
                  <div className="withdraw-fees-hint">
                    Simulando o saque...
                  </div>
                )}

                {quote.isReady && !quoteLoading && quoteError && (
                  <div className="withdraw-fees-error">
                    {quoteError.isRejectedDestination
                      ? "Não foi possível liberar este destino. Confira o endereço e a rede, ou fale com o suporte."
                      : quoteError.message}
                  </div>
                )}

                {!quoteLoading && quoteData && (
                  <>
                    <div className="withdraw-fees-row">
                      <span>Chega ao destino</span>
                      <span>
                        {formatQty(parseFloat(quoteData.amount))} {coinCode}
                      </span>
                    </div>
                    <div className="withdraw-fees-row">
                      <span>Taxa de rede ({quoteData.networkName})</span>
                      <span>
                        + {formatQty(parseFloat(quoteData.withdrawFee))}{" "}
                        {coinCode}
                      </span>
                    </div>
                    <div className="withdraw-fees-row withdraw-fees-row--total">
                      <span>Total debitado</span>
                      <span>
                        {formatQty(parseFloat(quoteData.totalDebit))} {coinCode}
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="withdraw-important">
              <span className="withdraw-important-icon" aria-hidden="true">⚠</span>
              <div>
                <div className="withdraw-important-title">Atenção</div>
                <div className="withdraw-important-text">
                  Confira o endereço e a rede selecionada. Transações on-chain
                  são irreversíveis — enviar para um endereço ou rede incorretos
                  pode resultar em perda permanente dos fundos.
                </div>
              </div>
            </div>

            {submitError && (
              <div className="withdraw-status withdraw-status--error">
                {submitError}
              </div>
            )}

            <button
              type="button"
              className="withdraw-submit"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitting ? "Solicitando..." : "Confirmar saque"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
